import { useUpdate } from "ahooks";
import { throttle as throttle_ } from "lodash";
import { useLayoutEffect, useRef } from "react";

export interface UseOverflowResult {
  isOverflow: boolean;
  isWidthOverflow: boolean;
  isHeightOverflow: boolean;
}
export function useIsOverflow(
  el?: HTMLElement | null,
  options: {
    onChange?: (data: UseOverflowResult) => void;
    throttle?: number;
    /**
     * if `onChange` is not provided, shouldUpdate will be set to true by default
     */
    shouldUpdate?: boolean;
  } = {}
): UseOverflowResult {
  const { onChange, throttle = 100, shouldUpdate } = options;

  const resultRef = useRef<UseOverflowResult>({
    isOverflow: false,
    isWidthOverflow: false,
    isHeightOverflow: false,
  });
  const update = useUpdate();

  function setResult(result: UseOverflowResult) {
    const curtResult = resultRef.current;
    if (
      curtResult &&
      curtResult.isOverflow === result.isOverflow &&
      curtResult.isWidthOverflow === result.isWidthOverflow &&
      curtResult.isHeightOverflow === result.isHeightOverflow
    ) {
      return;
    }

    resultRef.current = result;
    onChange?.(result);

    if (shouldUpdate ?? !onChange) {
      update();
    }
  }

  useLayoutEffect(() => {
    if (!el) {
      return;
    }

    const handle = throttle_(
      () => {
        const isWidthOverflow = el.scrollWidth > el.offsetWidth;
        const isHeightOverflow = el.scrollHeight > el.offsetHeight;
        const isOverflow = isWidthOverflow || isHeightOverflow;

        const newResult: UseOverflowResult = {
          isOverflow,
          isWidthOverflow,
          isHeightOverflow,
        };

        setResult(newResult);
      },
      throttle,
      { leading: true, trailing: true }
    );
    el.addEventListener("scroll", handle);
    handle();

    const observer = new ResizeObserver(handle);

    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", handle);
      observer.disconnect();
    };
  }, [el]);

  return resultRef.current;
}
